import React, { useState, useEffect, useCallback } from 'react';
import {
  MagnifyingGlassIcon,
  ArrowPathIcon,
  CheckCircleIcon,
  ChartBarIcon,
  ExclamationTriangleIcon
} from '@heroicons/react/24/outline';

import Card, { CardHeader } from '../ui/Card';
import { workspaceSearchService } from '../../services/api/workspaceSearchService';
import logger from '../../utils/logger';

// Canonical source ids accepted by the workspace-search backend.
// Checked = enabled; the UNCHECKED ones are stored as disabledSources.
const SOURCES = [
  { id: 'files', label: 'Files', description: 'Documents and files stored in Drive' },
  { id: 'email', label: 'Email', description: 'Mailbox messages and attachments' },
  { id: 'docs', label: 'Docs', description: 'Collaborative documents' },
  { id: 'sheets', label: 'Sheets', description: 'Spreadsheets and tabular data' },
];

const USAGE_DAYS = 30;

const toISODate = (date) => date.toISOString().slice(0, 10);

const WorkspaceSearchSettings = ({ showNotification }) => {
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState(false);
  const [saving, setSaving] = useState(false);
  const [unsavedChanges, setUnsavedChanges] = useState(false);

  const [searchEnabled, setSearchEnabled] = useState(true);
  const [enabledSources, setEnabledSources] = useState(
    SOURCES.reduce((acc, s) => ({ ...acc, [s.id]: true }), {})
  );

  const [usage, setUsage] = useState([]);
  const [usageError, setUsageError] = useState(false);

  const fetchAll = useCallback(async () => {
    setLoading(true);
    setLoadError(false);
    setUsageError(false);
    try {
      const to = new Date();
      const from = new Date();
      from.setDate(from.getDate() - (USAGE_DAYS - 1));

      const [settingsRes, usageRes] = await Promise.all([
        workspaceSearchService.getSettings(),
        workspaceSearchService.getUsage({ from: toISODate(from), to: toISODate(to) })
          .catch((error) => {
            logger.error('Error fetching workspace search usage:', error);
            setUsageError(true);
            return { success: false, data: [] };
          }),
      ]);

      if (settingsRes.success && settingsRes.data) {
        const disabled = settingsRes.data.disabledSources || [];
        setSearchEnabled(settingsRes.data.searchEnabled !== false);
        setEnabledSources(
          SOURCES.reduce((acc, s) => ({ ...acc, [s.id]: !disabled.includes(s.id) }), {})
        );
      }
      if (usageRes.success && Array.isArray(usageRes.data)) {
        setUsage(usageRes.data);
      }
      setUnsavedChanges(false);
    } catch (error) {
      logger.error('Error fetching workspace search settings:', error);
      setLoadError(true);
      showNotification && showNotification('Failed to load workspace search settings', 'error');
    } finally {
      setLoading(false);
    }
  }, [showNotification]);

  useEffect(() => {
    fetchAll();
  }, [fetchAll]);

  const handleSave = async () => {
    setSaving(true);
    try {
      const disabledSources = SOURCES.filter((s) => !enabledSources[s.id]).map((s) => s.id);
      const response = await workspaceSearchService.updateSettings({ searchEnabled, disabledSources });
      if (response.success) {
        setUnsavedChanges(false);
        showNotification && showNotification('Workspace search settings saved', 'success');
      } else {
        throw new Error('Failed to save workspace search settings');
      }
    } catch (error) {
      logger.error('Error saving workspace search settings:', error);
      showNotification && showNotification('Failed to save workspace search settings', 'error');
    } finally {
      setSaving(false);
    }
  };

  const toggleSource = (id) => {
    setEnabledSources((prev) => ({ ...prev, [id]: !prev[id] }));
    setUnsavedChanges(true);
  };

  const totalSearches = usage.reduce((sum, row) => sum + (row.searches || 0), 0);
  const totalTokens = usage.reduce((sum, row) => sum + (row.tokensUsed || 0), 0);

  if (loading) {
    return (
      <div className="flex items-center justify-center py-20">
        <div className="text-center">
          <div className="w-12 h-12 bg-orange-100 dark:bg-orange-900/30 rounded-xl flex items-center justify-center mx-auto mb-4">
            <ArrowPathIcon className="w-6 h-6 text-orange-600 dark:text-orange-400 animate-spin" />
          </div>
          <p className="text-sm font-medium text-gray-600 dark:text-gray-400">Loading workspace search settings...</p>
        </div>
      </div>
    );
  }

  if (loadError) {
    return (
      <Card className="p-6 bg-white dark:bg-[#1a1a1a] border border-gray-200 dark:border-gray-800 rounded-xl">
        <div className="text-center py-8">
          <ExclamationTriangleIcon className="w-12 h-12 text-yellow-500 mx-auto mb-3" />
          <p className="text-sm text-gray-600 dark:text-gray-400 mb-4">Failed to load workspace search settings</p>
          <button
            onClick={fetchAll}
            className="inline-flex items-center gap-1.5 px-3 py-1.5 bg-orange-600 hover:bg-orange-700 text-white rounded-md text-xs font-medium transition-colors"
          >
            <ArrowPathIcon className="w-3.5 h-3.5" />
            Retry
          </button>
        </div>
      </Card>
    );
  }

  return (
    <div className="space-y-6">
      {/* Settings */}
      <Card className="p-6 bg-white dark:bg-[#1a1a1a] border border-gray-200 dark:border-gray-800 rounded-xl">
        <div className="flex items-center justify-between mb-6">
          <div className="flex items-center space-x-3">
            <div className="w-10 h-10 bg-gradient-to-br from-orange-500 to-orange-600 rounded-xl flex items-center justify-center shadow-sm">
              <MagnifyingGlassIcon className="w-5 h-5 text-white" />
            </div>
            <div>
              <h3 className="text-lg font-semibold text-gray-900 dark:text-white">Workspace Search</h3>
              <p className="text-sm text-gray-500 dark:text-gray-400">Control AI search across your workspace data sources</p>
            </div>
          </div>
          <button
            onClick={handleSave}
            disabled={saving || !unsavedChanges}
            className="flex items-center gap-1.5 px-3 py-1.5 bg-orange-600 hover:bg-orange-700 disabled:bg-gray-300 dark:disabled:bg-[#222222] text-white rounded-md text-xs font-medium transition-colors disabled:cursor-not-allowed"
          >
            <CheckCircleIcon className="w-3.5 h-3.5" />
            {saving ? 'Saving...' : 'Save'}
          </button>
        </div>

        <div className="space-y-4">
          <div className="flex items-center justify-between p-4 bg-gray-50 dark:bg-[#222222] rounded-lg">
            <div className="flex-1">
              <label className="text-sm font-medium text-gray-900 dark:text-white">Enable Workspace Search</label>
              <p className="text-xs text-gray-500 dark:text-gray-400 mt-0.5">Master switch for workspace search across all sources</p>
            </div>
            <label className="relative inline-flex items-center cursor-pointer">
              <input
                type="checkbox"
                checked={searchEnabled}
                onChange={(e) => { setSearchEnabled(e.target.checked); setUnsavedChanges(true); }}
                className="sr-only peer"
              />
              <div className="w-11 h-6 bg-gray-300 dark:bg-[#222222] peer-focus:outline-none peer-focus:ring-2 peer-focus:ring-orange-500 dark:peer-focus:ring-orange-600 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 dark:after:border-gray-700 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-orange-600 shadow-inner"></div>
            </label>
          </div>

          <div className="p-4 bg-gray-50 dark:bg-[#222222] rounded-lg">
            <label className="text-sm font-medium text-gray-900 dark:text-white">Searchable Sources</label>
            <p className="text-xs text-gray-500 dark:text-gray-400 mt-0.5 mb-3">Unchecked sources are excluded from search results</p>
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-3">
              {SOURCES.map((source) => (
                <label
                  key={source.id}
                  className={`flex items-start gap-3 p-3 bg-white dark:bg-[#1a1a1a] border border-gray-200 dark:border-gray-800 rounded-lg cursor-pointer hover:bg-gray-100 dark:hover:bg-[#2a2a2a] transition-colors ${!searchEnabled ? 'opacity-50' : ''}`}
                >
                  <input
                    type="checkbox"
                    checked={enabledSources[source.id]}
                    onChange={() => toggleSource(source.id)}
                    className="mt-0.5 w-4 h-4 text-orange-600 border-gray-300 dark:border-gray-700 rounded focus:ring-orange-500"
                  />
                  <span className="flex-1">
                    <span className="block text-sm font-medium text-gray-900 dark:text-white">{source.label}</span>
                    <span className="block text-xs text-gray-500 dark:text-gray-400 mt-0.5">{source.description}</span>
                  </span>
                </label>
              ))}
            </div>
          </div>
        </div>
      </Card>

      {/* Usage (last 30 days) */}
      <Card className="p-6 bg-white dark:bg-[#1a1a1a] border border-gray-200 dark:border-gray-800 rounded-xl">
        <CardHeader>
          <div className="flex items-center space-x-3">
            <div className="w-10 h-10 bg-gradient-to-br from-blue-500 to-blue-600 rounded-xl flex items-center justify-center shadow-sm">
              <ChartBarIcon className="w-5 h-5 text-white" />
            </div>
            <div>
              <h3 className="text-lg font-semibold text-gray-900 dark:text-white">Usage — Last {USAGE_DAYS} Days</h3>
              <p className="text-sm text-gray-500 dark:text-gray-400">
                {usageError ? 'Usage data is currently unavailable' : `${totalSearches.toLocaleString()} searches • ${totalTokens.toLocaleString()} tokens used`}
              </p>
            </div>
          </div>
        </CardHeader>

        {usageError ? (
          <div className="text-center py-8">
            <ExclamationTriangleIcon className="w-12 h-12 text-yellow-500 mx-auto mb-3" />
            <p className="text-sm text-gray-500 dark:text-gray-400">Failed to load usage data</p>
          </div>
        ) : usage.length > 0 ? (
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b border-gray-200 dark:border-gray-800">
                  <th className="text-left py-2 px-3 text-xs font-medium text-gray-500 dark:text-gray-400 uppercase">Date</th>
                  <th className="text-right py-2 px-3 text-xs font-medium text-gray-500 dark:text-gray-400 uppercase">Searches</th>
                  <th className="text-right py-2 px-3 text-xs font-medium text-gray-500 dark:text-gray-400 uppercase">Tokens Used</th>
                </tr>
              </thead>
              <tbody>
                {usage.map((row) => (
                  <tr key={row.date} className="border-b border-gray-100 dark:border-gray-800/50 hover:bg-gray-50 dark:hover:bg-[#222222] transition-colors">
                    <td className="py-2 px-3 text-gray-900 dark:text-white">{row.date}</td>
                    <td className="py-2 px-3 text-right text-gray-900 dark:text-white">{(row.searches || 0).toLocaleString()}</td>
                    <td className="py-2 px-3 text-right text-gray-900 dark:text-white">{(row.tokensUsed || 0).toLocaleString()}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        ) : (
          <div className="text-center py-8">
            <ChartBarIcon className="w-12 h-12 text-gray-300 dark:text-gray-600 mx-auto mb-3" />
            <p className="text-sm text-gray-500 dark:text-gray-400">No searches in the last {USAGE_DAYS} days</p>
          </div>
        )}
      </Card>
    </div>
  );
};

export default WorkspaceSearchSettings;
